import { useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { login as loginRequest, type AuthApiError } from "../lib/auth";
import { BrandMark } from "../branding/Brand";
import { useBranding } from "../branding/brandingStore";
import { MicrosoftMark } from "../components/ui/Icon";
import { CopilotLogo } from "../components/ui/CopilotLogo";
import "./login.css";

/**
 * The sign-in screen, drawn as the canvas's login layout A: a brand-coloured
 * identity panel carrying the deployment's own logo and one sentence about what
 * this is, beside a single card on a muted ground.
 *
 * Microsoft 365 SSO is the primary action and is always offered first. Local
 * email and password sit below a rule, and their submit is the SECONDARY
 * button — there is one primary action on this screen, and it is not the
 * fallback. Below 900px the identity panel drops and the card takes the width;
 * that is the one media query the design admits.
 */
interface LoginPageProps {
  /** Test/preview override; production defaults to VITE_SSO_ONLY. */
  ssoOnly?: boolean;
}

const ENV_SSO_ONLY = import.meta.env.VITE_SSO_ONLY === "true";

interface LoginLocationState {
  from?: string;
  sessionExpired?: boolean;
}

export function LoginPage({ ssoOnly = ENV_SSO_ONLY }: LoginPageProps = {}): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const branding = useBranding();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loginState = location.state as LoginLocationState | null;
  const from = loginState?.from ?? "/dashboard";

  /**
   * Why the last SSO attempt failed.
   *
   * The Entra callback redirects here as `/login?auth_error=CODE` on every
   * refusal it makes — wrong directory, no account provisioned, an address
   * already linked to another identity. Nothing read the parameter, so all of
   * those arrived as a silent bounce back to a sign-in screen that looked
   * exactly as it had a moment earlier: the user retried, failed identically,
   * and reported "it does not work" with nothing anyone could act on. The
   * reason is on the server's own audit trail either way; showing it here is
   * what turns it into something the person can take to an administrator.
   */
  const [searchParams] = useSearchParams();
  const ssoError = searchParams.get("auth_error");

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await loginRequest({ email, password, rememberMe });
      // Refresh the current-user query so the app re-renders as authenticated.
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      navigate(from, { replace: true });
    } catch (err) {
      const code = (err as AuthApiError).code;
      setError(messageFor(code, (err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-shell">
      {/* One mark and one sentence, on the brand colour. No gradient, no dot
          grid, no blur — the navy is the whole statement here. The deployment
          logo used to sit at the top edge as well; two logos on one surface is
          one too many, and the card's own mark already carries the identity. */}
      <section
        className="login-identity"
        data-login-identity
        data-on-brand
        aria-label={branding.brandName}
      >
        <div className="login-identity-copy">
          <CopilotLogo className="login-identity-mark" size={180} label="Microsoft 365 Copilot" />
          <p>
            Microsoft 365 Copilot for your organization&apos;s files, mail, meetings
            and connected business systems.
          </p>
        </div>
        <p className="login-identity-foot">Authorized personnel only · IT Support</p>
      </section>

      <main className="login-panel">
        <div className="login-card">
          {/* The compact mark, not the full logo: one logo per surface. */}
          <BrandMark className="login-card-mark" size={76} />

          <h1 className="login-title">Sign in</h1>
          <p className="login-subtitle">Use your organizational Microsoft 365 account.</p>

          {ssoError && (
            <p className="login-sso-error" role="alert" data-testid="login-sso-error">
              {ssoMessageFor(ssoError)}
            </p>
          )}

          {loginState?.sessionExpired && (
            <p className="login-session-notice" role="status" data-testid="session-expired-notice">
              Your session expired. Please sign in again.
            </p>
          )}

          <a className="login-sso" href="/api/v1/auth/entra/login" data-testid="login-sso">
            <MicrosoftMark size={16} />
            Sign in with Microsoft 365
          </a>

          {ssoOnly ? (
            <p className="login-sso-note">
              Single sign-on is required for this deployment.
            </p>
          ) : (
            <>
              <div className="login-divider">
                <span />
                OR
                <span />
              </div>

              <form
                onSubmit={(e) => void onSubmit(e)}
                className="login-form"
                data-testid="local-login-form"
              >
                <label className="login-field">
                  <span>Email</span>
                  <input
                    type="email"
                    autoComplete="username"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email"
                  />
                </label>
                <label className="login-field">
                  <span>Password</span>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password"
                  />
                </label>

                <div className="login-row">
                  <label className="login-remember">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    Remember me
                  </label>
                  <button
                    type="button"
                    className="login-forgot"
                    onClick={() =>
                      setError(
                        "Password reset is not available yet. Contact your administrator.",
                      )
                    }
                  >
                    Forgot password?
                  </button>
                </div>

                {error && (
                  <p className="login-error" role="alert" data-testid="login-error">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  className="login-submit"
                  disabled={busy}
                  data-testid="login-submit"
                >
                  {busy ? "Signing in…" : "Sign in"}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Repeated at the foot of the card column, because the identity panel
            that carries it is gone below 900px. */}
        <p className="login-authorized">Authorized personnel only · IT Support</p>
      </main>
    </div>
  );
}

/**
 * The Entra callback's refusal codes, in words.
 *
 * Each one says what happened AND who can fix it, because every case here is
 * resolved by somebody other than the person reading it — there is no retry
 * that helps. The unknown-code fallback stays vague on purpose: it is reached
 * when the server refused for a reason this build does not know about, and
 * inventing a specific cause would be worse than admitting to none.
 */
function ssoMessageFor(code: string): string {
  switch (code) {
    case "NOT_PROVISIONED":
      return (
        "You do not have an account on this deployment yet. Ask an administrator " +
        "to invite you, then sign in again."
      );
    case "ACCOUNT_UNAVAILABLE":
      return "This account has been suspended or disabled. Contact your administrator.";
    case "TENANT_NOT_ALLOWED":
      return "This deployment does not accept sign-ins from your Microsoft directory.";
    case "EMAIL_ALREADY_LINKED":
      return (
        "An account already exists for your email address and is linked to a " +
        "different Microsoft identity. An administrator must resolve it."
      );
    case "AUTH_STATE_INVALID":
      return "That sign-in attempt expired before it finished. Please try again.";
    case "ENTRA_NOT_CONFIGURED":
      return "Microsoft sign-in is not configured on this server.";
    default:
      return "Microsoft sign-in did not complete. Please try again, or contact your administrator.";
  }
}

function messageFor(code: string | undefined, fallback: string): string {
  switch (code) {
    case "INVALID_CREDENTIALS":
      return "Invalid email or password.";
    case "ACCOUNT_LOCKED":
      return "This account is temporarily locked after too many failed attempts. Try again later.";
    case "ACCOUNT_DISABLED":
      return "This account is disabled. Contact your administrator.";
    case "RATE_LIMITED":
      return "Too many attempts. Please wait a moment and try again.";
    default:
      return fallback || "Sign in failed. Please try again.";
  }
}
